import { RoutePermissionCheck, WellRoutePublicPublishedPermissionsCheck } from './components/route-definitions'
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import KeycloakWrapper from './services/keycloak'
import ProvideUserContext, { UserContext } from './state/user-state'
import ReactModal from 'react-modal'
import LandingPage from './routes'
import FakeAuthTokenCatcher from './routes/fake-auth'
import Page from '../app/components/page'
import ViewWellExportReportDetails from './routes/wells/[id]/export-view/details'
import { StrictMode, useEffect } from 'react'
import TermsOfUse from './routes/terms-of-use'
import { SCROLL_TARGET } from './services/constants'
import AppErrorBoundary from './components/errors/components/error-boundary'
import { ErrorCode } from './components/errors/constants'
import usePageViews from './hooks/use-page-views'
import ReactGA from 'react-ga4'
import { GA_MEASUREMENT_ID } from './config/constants'
import { Permissions } from '../types/user-types'
import Dashboard from './routes/dashboard'
import Administration from './routes/admin'
import Search from './routes/wells'
import ViewWell from './routes/wells/[id]/view'
import EditWell from './routes/wells/[id]/edit'
import ContactUs from './routes/contact-us'
import HelpCentre from './routes/help-centre'
import AdminUsers from './routes/admin/users'
import EditWellsUser from './routes/admin/users/[id]/edit'
import ViewWellsUser from './routes/admin/users/[id]/view'
import Organisations from './routes/admin/organisations'
import EditOrganisation from './routes/admin/organisations/[id]/edit'
import Organisation from './routes/admin/organisations/[id]/view'
import { WorkflowContainer } from './components/workflow-container'
import EditWellReviewWrapped from './routes/wells/[id]/edit/review'
import ViewWellHistory from './routes/wells/[id]/view/history'
import EditWellHistory from './routes/wells/[id]/edit/history'
import ViewWellHistoryDetails from './routes/wells/[id]/view/history/[id]'
import EditWellHistoryDetails from './routes/wells/[id]/edit/history/[id]'
import WellSetupWrapped from './routes/wells/[id]/edit/setup'
import ViewWellSummaryDetails from './routes/wells/[id]/view/summary-details'
import ViewWellDetails from './routes/wells/[id]/view/details'
import ViewWellConstruction from './routes/wells/[id]/view/construction'
import ViewWellGeothermal from './routes/wells/[id]/view/geothermal'
import WellStratumView from './routes/wells/[id]/view/lithology/stratum/[id]'
import ViewWellLithology from './routes/wells/[id]/view/lithology'
import ViewWellHeadSecurity from './routes/wells/[id]/view/wellhead-security'
import ViewWellDrinkingWater from './routes/wells/[id]/view/drinking-water'
import ViewWellAquifer from './routes/wells/[id]/view/aquifer'
import ViewWellDrillingTests from './routes/wells/[id]/view/drilling-tests'
import ViewWellAquiferTests from './routes/wells/[id]/view/aquifer-tests'
import AquiferTestView from './routes/wells/[id]/view/aquifer-tests/[id]'
import ViewWellAdditionalDocuments from './routes/wells/[id]/view/additional-documents'
import ViewExternalDocumentReference from './routes/wells/[id]/view/external-document-reference/[id]'
import ViewSubmissionPage from './routes/wells/[id]/view/submission-wrapped'
import NewWellApprove from './routes/wells/[id]/edit/approval'
import ListExternalDocumentReferences from './routes/wells/[id]/view/external-document-references'
import NotFound from './components/errors/components/not-found'
import EditWellDetailsWrapped from './routes/wells/[id]/edit/details'
import EditWellConstructionWrapped from './routes/wells/[id]/edit/construction'
import EditWellGeothermalWrapped from './routes/wells/[id]/edit/geothermal'
import EditWellLithology from './routes/wells/[id]/edit/lithology'
import EditWellWellheadSecurityWrapped from './routes/wells/[id]/edit/wellhead-security'
import EditWellAquiferWrapped from './routes/wells/[id]/edit/aquifer'
import EditWellDrillingTests from './routes/wells/[id]/edit/drilling-tests'
import DrillingTestEditWrapped from './routes/wells/[id]/edit/drilling-tests/[id]'
import { EditWellAquiferTests } from './routes/wells/[id]/edit/aquifer-tests'
import EditAquiferTestWrapped from './routes/wells/[id]/edit/aquifer-tests/[id]'
import EditWellAdditionalDocumentsWrapped from './routes/wells/[id]/edit/additional-documents'
import EditSubmissionPage from './routes/wells/[id]/edit/submission-wrapper'
import EditStratumWrapped from './routes/wells/[id]/edit/lithology/stratum/[id]'
import EditWellDrinkingWaterWrapped from './routes/wells/[id]/edit/drinking-water'
import DrillingTestView from './routes/wells/[id]/view/drilling-tests/[id]'
import EditExternalDocumentReferenceWrapped from './routes/wells/[id]/edit/external-document-reference/[id]'
import { UserAPIClients } from './routes/user/api-clients'
import { ScrollToTop, ToastContainer } from '@msaf/core-react'
import { ClientCache } from '@msaf/core-common'

if (GA_MEASUREMENT_ID) {
  ReactGA.initialize(GA_MEASUREMENT_ID)
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      useErrorBoundary: true,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
      useErrorBoundary: (error: any) =>
        [ErrorCode.NOT_FOUND, ErrorCode.PERMISSION_DENIED, ErrorCode.SESSION_TIMEOUT, ErrorCode.SERVER_ERROR].includes(
          error.message,
        ),
    },
  },
})

ReactModal.setAppElement('#root')

const router = createBrowserRouter([
  {
    element: <Content />,
    children: [
      { path: '/', element: <LandingPage /> },
      { path: '/terms-of-use', element: <TermsOfUse /> },
      { path: '/fake-auth/*', element: <FakeAuthTokenCatcher /> },
      {
        path: '/export-view/:id/details',
        element: (
          <RoutePermissionCheck permissions={[Permissions.VIEW_WELL]}>
            <ViewWellExportReportDetails />
          </RoutePermissionCheck>
        ),
      },
      {
        element: (
          <Page>
            <Outlet />
          </Page>
        ),
        children: [
          {
            path: '/dashboard',
            element: (
              <RoutePermissionCheck permissions={[Permissions.VIEW_WELL]}>
                <Dashboard />
              </RoutePermissionCheck>
            ),
          },
          {
            path: '/user/api-clients',
            element: (
              <RoutePermissionCheck permissions={[Permissions.MAINTAIN_API_CLIENTS]}>
                <UserAPIClients />
              </RoutePermissionCheck>
            ),
          },
          {
            path: '/admin/*',
            element: (
              <RoutePermissionCheck permissions={[Permissions.MAINTAIN_ALL_USERS, Permissions.MAINTAIN_ORG_USERS]}>
                <Administration />
              </RoutePermissionCheck>
            ),
            children: [
              {
                path: 'users',
                element: <AdminUsers />,
              },
              {
                path: 'users/:id/edit',
                element: <EditWellsUser />,
              },
              {
                path: 'users/:id/view',
                element: <ViewWellsUser />,
              },
              {
                path: 'organisations',
                element: <Organisations />,
              },
              {
                path: 'organisations/:id/edit',
                element: <EditOrganisation />,
              },
              {
                path: 'organisations/:id/view',
                element: <Organisation />,
              },
            ],
          },
          {
            path: '/wells/:mode?',
            element: (
              <RoutePermissionCheck isPublic={true}>
                <Search />
              </RoutePermissionCheck>
            ),
          },
          {
            path: '/wells/:id/view/*',
            element: (
              <RoutePermissionCheck isPublic={true}>
                <ViewWell backLink={{ path: '/wells', label: 'Wells' }} />
              </RoutePermissionCheck>
            ),
            children: [
              {
                path: 'history',
                children: [
                  {
                    index: true,
                    element: (
                      <RoutePermissionCheck permissions={[Permissions.VIEW_WELL]}>
                        <ViewWellHistory />
                      </RoutePermissionCheck>
                    ),
                  },
                  {
                    path: ':historyId/details',
                    element: (
                      <RoutePermissionCheck permissions={[Permissions.VIEW_WELL]}>
                        <ViewWellHistoryDetails />
                      </RoutePermissionCheck>
                    ),
                  },
                ],
              },
              {
                element: (
                  <WorkflowContainer>
                    <Outlet />
                  </WorkflowContainer>
                ),
                children: [
                  {
                    path: 'drinking-water-supply',
                    element: (
                      <RoutePermissionCheck permissions={[Permissions.VIEW_WELL]}>
                        <ViewWellDrinkingWater />
                      </RoutePermissionCheck>
                    ),
                  },
                  {
                    path: 'external-document-references',
                    children: [
                      {
                        index: true,
                        element: (
                          <WellRoutePublicPublishedPermissionsCheck permissions={[Permissions.VIEW_WELL]}>
                            <ListExternalDocumentReferences />
                          </WellRoutePublicPublishedPermissionsCheck>
                        ),
                      },
                      {
                        path: ':externalDocumentReferencesId',
                        element: (
                          <RoutePermissionCheck permissions={[Permissions.VIEW_ADDITIONAL_DOCUMENTS]}>
                            <ViewExternalDocumentReference />
                          </RoutePermissionCheck>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'approve',
                    element: (
                      <RoutePermissionCheck permissions={[Permissions.CREATE_WELL, Permissions.CREATE_WELL_UNSCOPED]}>
                        <NewWellApprove />
                      </RoutePermissionCheck>
                    ),
                  },
                  {
                    element: (
                      <WellRoutePublicPublishedPermissionsCheck permissions={[Permissions.VIEW_WELL]}>
                        <Outlet />
                      </WellRoutePublicPublishedPermissionsCheck>
                    ),
                    children: [
                      {
                        path: 'summary-details',
                        element: <ViewWellSummaryDetails />,
                      },
                      {
                        path: 'details',
                        element: <ViewWellDetails />,
                      },
                      {
                        path: 'construction',
                        element: <ViewWellConstruction />,
                      },
                      {
                        path: 'geothermal',
                        element: <ViewWellGeothermal />,
                      },
                      {
                        path: 'geothermal',
                        element: <ViewWellGeothermal />,
                      },
                      {
                        path: 'lithology',
                        children: [
                          {
                            index: true,
                            element: <ViewWellLithology />,
                          },
                          {
                            path: 'stratum/:stratumId',
                            element: <WellStratumView />,
                          },
                        ],
                      },
                      {
                        path: 'wellhead-security',
                        element: <ViewWellHeadSecurity />,
                      },
                      {
                        path: 'aquifer',
                        element: <ViewWellAquifer />,
                      },
                      {
                        path: 'drilling-tests',
                        children: [
                          {
                            index: true,
                            element: <ViewWellDrillingTests />,
                          },
                          {
                            path: ':testId',
                            element: <DrillingTestView />,
                          },
                        ],
                      },
                      {
                        path: 'aquifer-tests',
                        children: [
                          {
                            index: true,
                            element: <ViewWellAquiferTests />,
                          },
                          {
                            path: ':aquiferTestId',
                            element: <AquiferTestView />,
                          },
                        ],
                      },
                      {
                        path: 'additional-documents',
                        element: <ViewWellAdditionalDocuments />,
                      },
                      {
                        path: 'submission',
                        element: <ViewSubmissionPage />,
                      },
                    ],
                  },
                  {
                    path: '*',
                    element: <NotFound />,
                  },
                ],
              },
            ],
          },
          {
            path: '/wells/:id/edit/*',
            element: (
              <RoutePermissionCheck isPublic={true}>
                <EditWell backLink={{ path: '/wells', label: 'Wells' }} />
              </RoutePermissionCheck>
            ),
            children: [
              {
                path: 'history',
                children: [
                  {
                    index: true,
                    element: (
                      <RoutePermissionCheck permissions={[Permissions.EDIT_WELL]}>
                        <EditWellHistory />
                      </RoutePermissionCheck>
                    ),
                  },
                  {
                    path: ':historyId/details',
                    element: (
                      <RoutePermissionCheck permissions={[Permissions.EDIT_WELL]}>
                        <EditWellHistoryDetails />
                      </RoutePermissionCheck>
                    ),
                  },
                ],
              },
              {
                element: (
                  <WorkflowContainer>
                    <Outlet />
                  </WorkflowContainer>
                ),
                children: [
                  {
                    path: 'review/*',
                    element: (
                      <RoutePermissionCheck permissions={[Permissions.REVIEW_WELL]}>
                        <EditWellReviewWrapped />
                      </RoutePermissionCheck>
                    ),
                  },
                  {
                    path: 'setup',
                    element: (
                      <RoutePermissionCheck permissions={[Permissions.CREATE_WELL, Permissions.CREATE_WELL_UNSCOPED]}>
                        <WellSetupWrapped />
                      </RoutePermissionCheck>
                    ),
                  },
                  {
                    path: 'drinking-water-supply',
                    element: (
                      <RoutePermissionCheck
                        permissions={[
                          Permissions.EDIT_WELL,
                          Permissions.EDIT_WELL_UNSCOPED,
                          Permissions.VIEW_DRINKING_WATER_INFO,
                        ]}
                      >
                        <EditWellDrinkingWaterWrapped />
                      </RoutePermissionCheck>
                    ),
                  },
                  {
                    path: 'external-document-references',
                    children: [
                      {
                        path: ':externalDocumentReferencesId',
                        element: (
                          <RoutePermissionCheck permissions={[Permissions.EDIT_EXTERNAL_DOCUMENTS]}>
                            <EditExternalDocumentReferenceWrapped />
                          </RoutePermissionCheck>
                        ),
                      },
                    ],
                  },
                  {
                    element: (
                      <RoutePermissionCheck permissions={[Permissions.EDIT_WELL, Permissions.EDIT_WELL_UNSCOPED]}>
                        <Outlet />
                      </RoutePermissionCheck>
                    ),
                    children: [
                      {
                        path: 'details',
                        element: <EditWellDetailsWrapped />,
                      },
                      {
                        path: 'construction',
                        element: <EditWellConstructionWrapped />,
                      },
                      {
                        path: 'geothermal',
                        element: <EditWellGeothermalWrapped />,
                      },
                      {
                        path: 'lithology',
                        children: [
                          {
                            index: true,
                            element: <EditWellLithology />,
                          },
                          {
                            path: 'stratum/:stratumId',
                            element: <EditStratumWrapped />,
                          },
                        ],
                      },
                      {
                        path: 'wellhead-security',
                        element: <EditWellWellheadSecurityWrapped />,
                      },
                      {
                        path: 'aquifer',
                        element: <EditWellAquiferWrapped />,
                      },
                      {
                        path: 'drilling-tests',
                        children: [
                          {
                            index: true,
                            element: <EditWellDrillingTests />,
                          },
                          {
                            path: ':testId',
                            element: <DrillingTestEditWrapped />,
                          },
                        ],
                      },
                      {
                        path: 'aquifer-tests',
                        children: [
                          {
                            index: true,
                            element: <EditWellAquiferTests />,
                          },
                          {
                            path: ':aquiferTestId',
                            element: <EditAquiferTestWrapped />,
                          },
                        ],
                      },
                      {
                        path: 'additional-documents',
                        element: <EditWellAdditionalDocumentsWrapped />,
                      },
                      {
                        path: 'submission',
                        element: <EditSubmissionPage />,
                      },
                    ],
                  },
                  {
                    path: '*',
                    element: <NotFound />,
                  },
                ],
              },
            ],
          },
          {
            path: '/contact-us',
            element: (
              <RoutePermissionCheck isPublic={true}>
                <ContactUs />
              </RoutePermissionCheck>
            ),
          },
          {
            path: '/help-centre',
            element: (
              <RoutePermissionCheck isPublic={true}>
                <HelpCentre />
              </RoutePermissionCheck>
            ),
          },
        ],
      },
    ],
  },
])

function App() {
  useEffect(() => {
    // Initialise the app cache
    // `ClientCache` can be used to persist values across routes
    ClientCache.getInstance()
    // Clear cache on unmount
    return () => ClientCache.getInstance().flush()
  }, [])

  return (
    <AppErrorBoundary>
      <ProvideUserContext>
        <a href='#a11y-main-content' className='c-skip-link'>
          Skip to content
        </a>
        <KeycloakWrapper userContext={UserContext}>
          <StrictMode>
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
            </QueryClientProvider>
          </StrictMode>
        </KeycloakWrapper>
      </ProvideUserContext>
    </AppErrorBoundary>
  )
}

function Content() {
  usePageViews()
  return (
    <>
      <ScrollToTop targetDiv={SCROLL_TARGET} />
      <Outlet />
      {/* Container for toast messages (needs to be rendered once in application tree) */}
      <ToastContainer />
    </>
  )
}

export default App
